import { useState, useEffect, useCallback, useRef } from 'react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { useWorkspaceStore } from '@/store/workspaceStore'
import { userAPI, lcaAPI } from '@/lib/api/client'
import { getApiErrorMessage } from '@/lib/api/errors'
import { toast } from 'sonner'
import { useProjectList } from '@/hooks/workspace/useProjectList'
import type { ToolCallEvent } from '@/hooks/workspace/useWorkspaceSocket'
import type { ActivityWithExchanges } from '@/lib/types/project-graph'
import {
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen,
  Box,
  ArrowRightLeft,
  Search,
  Plus,
  RefreshCw,
  Database,
  Check,
  Globe,
  LockKeyhole,
  Copy,
  ExternalLink,
  Loader2,
  Link2 as LinkIcon,
  Trash2,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { AddActivityDialog } from './AddActivityDialog'

interface ProjectExplorerProps {
  activities: ActivityWithExchanges[]
  selectedProjectId: string | null
  onProjectSelect: (projectId: string) => void
  isLoadingProject?: boolean
  onActivityCreated?: () => void
  /** WebSocket events — drives auto-refresh of the project list. */
  wsEvents?: ToolCallEvent[]
}

export function ProjectExplorer({
  activities,
  selectedProjectId,
  onProjectSelect,
  isLoadingProject,
  onActivityCreated,
  wsEvents,
}: ProjectExplorerProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [projectFilter, setProjectFilter] = useState('')
  const [expandedActivities, setExpandedActivities] = useState<Set<string>>(new Set())
  const [projectsExpanded, setProjectsExpanded] = useState(true)
  const [addActivityOpen, setAddActivityOpen] = useState(false)
  const [deletingProjectId, setDeletingProjectId] = useState<string | null>(null)
  // User-resizable height for the Projects section (px). Persisted across renders
  // via local state — survives tab switches but resets on reload, which is fine.
  const [projectsHeight, setProjectsHeight] = useState(() => {
    const saved = localStorage.getItem('workspace-projects-section-height')
    return saved ? Math.max(120, Math.min(600, Number(saved))) : 200
  })
  const dragRef = useRef<{ startY: number; startH: number } | null>(null)
  const { selectedNodeId, selectNode } = useWorkspaceStore()

  const onResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      dragRef.current = { startY: e.clientY, startH: projectsHeight }
      const onMove = (ev: MouseEvent) => {
        if (!dragRef.current) return
        const next = Math.max(
          120,
          Math.min(600, dragRef.current.startH + ev.clientY - dragRef.current.startY),
        )
        setProjectsHeight(next)
      }
      const onUp = () => {
        if (dragRef.current) {
          localStorage.setItem('workspace-projects-section-height', String(projectsHeight))
        }
        dragRef.current = null
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
      }
      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
    },
    [projectsHeight],
  )

  // Project list — owned by useProjectList (initial fetch + WS-driven refetch
  // on create_project / update_project / delete_project).
  const {
    projects,
    loading: loadingProjects,
    refetch: fetchProjects,
  } = useProjectList({
    events: wsEvents,
  })

  const handleDeleteProject = useCallback(
    async (projectId: string, projectName: string) => {
      setDeletingProjectId(projectId)
      try {
        await lcaAPI.deleteProject(projectId)
        toast.success(`Project "${projectName}" deleted`)
        if (selectedProjectId === projectId) {
          onProjectSelect('')
        }
        fetchProjects()
      } catch (err) {
        toast.error(getApiErrorMessage(err, 'Failed to delete project'))
      } finally {
        setDeletingProjectId(null)
      }
    },
    [selectedProjectId, onProjectSelect, fetchProjects],
  )

  // Live link state
  interface LiveLink {
    share_id: string
    project_id: string
    is_public: boolean
    url: string
  }
  const [liveLinks, setLiveLinks] = useState<Map<string, LiveLink>>(new Map())
  const [expandedShareId, setExpandedShareId] = useState<string | null>(null)
  const [copiedShareId, setCopiedShareId] = useState<string | null>(null)
  const [creatingLinkFor, setCreatingLinkFor] = useState<string | null>(null)

  const fetchLiveLinks = useCallback(async () => {
    try {
      const resp = await userAPI.getMyLiveLinks()
      const map = new Map<string, LiveLink>()
      for (const link of resp.data.links || []) {
        map.set(link.project_id, link)
      }
      setLiveLinks(map)
    } catch {
      /* ignore — not critical */
    }
  }, [])

  const toggleLinkVisibility = useCallback(async (shareId: string) => {
    try {
      const resp = await userAPI.toggleLiveLinkVisibility(shareId)
      setLiveLinks((prev) => {
        const next = new Map(prev)
        for (const [pid, link] of next) {
          if (link.share_id === shareId) {
            next.set(pid, { ...link, is_public: resp.data.is_public })
          }
        }
        return next
      })
    } catch {
      /* ignore */
    }
  }, [])

  const copyLinkUrl = useCallback((link: LiveLink) => {
    navigator.clipboard
      .writeText(link.url)
      .then(() => {
        setCopiedShareId(link.share_id)
        setTimeout(() => setCopiedShareId(null), 2000)
      })
      .catch(() => {})
  }, [])

  const createLiveLink = useCallback(async (projectId: string) => {
    setCreatingLinkFor(projectId)
    try {
      const resp = await userAPI.createLiveLink(projectId)
      const { share_id, url } = resp.data
      setLiveLinks((prev) => {
        const next = new Map(prev)
        next.set(projectId, { share_id, project_id: projectId, is_public: false, url })
        return next
      })
    } catch {
      /* ignore */
    } finally {
      setCreatingLinkFor(null)
    }
  }, [])

  useEffect(() => {
    fetchLiveLinks()
  }, [fetchLiveLinks])

  const toggleActivity = (activityId: string) => {
    setExpandedActivities((prev) => {
      const next = new Set(prev)
      if (next.has(activityId)) {
        next.delete(activityId)
      } else {
        next.add(activityId)
      }
      return next
    })
  }

  // Filter activities by search query
  const filteredActivities = activities.filter((activity) => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return (
      activity.name.toLowerCase().includes(query) ||
      activity.code.toLowerCase().includes(query) ||
      activity.location?.toLowerCase().includes(query)
    )
  })

  const selectedProject = projects.find((p) => p.id === selectedProjectId)

  return (
    <div className="h-full flex flex-col">
      {/* Project Selector Section */}
      <div className="border-b border-border">
        <Collapsible open={projectsExpanded} onOpenChange={setProjectsExpanded}>
          <CollapsibleTrigger className="flex items-center justify-between w-full px-2 py-1.5 hover:bg-accent/50 transition-colors">
            <div className="flex items-center gap-1.5">
              {projectsExpanded ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
              <Database className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs font-medium">Projects</span>
              <span className="text-[10px] text-muted-foreground">({projects.length})</span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5"
              onClick={(e) => {
                e.stopPropagation()
                fetchProjects()
              }}
              disabled={loadingProjects}
            >
              <RefreshCw className={cn('h-3 w-3', loadingProjects && 'animate-spin')} />
            </Button>
          </CollapsibleTrigger>

          <CollapsibleContent>
            {projects.length > 5 && (
              <div className="px-2 pt-1 pb-1.5">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                  <Input
                    placeholder="Filter projects..."
                    value={projectFilter}
                    onChange={(e) => setProjectFilter(e.target.value)}
                    className="h-7 pl-7 text-xs"
                  />
                </div>
              </div>
            )}
            <div
              className="px-2 pb-2 space-y-0.5 overflow-y-auto"
              style={{ maxHeight: projectsHeight }}
            >
              {projects.length === 0 ? (
                <p className="text-xs text-muted-foreground py-2 text-center">
                  {loadingProjects ? 'Loading...' : 'No projects found'}
                </p>
              ) : (
                projects
                  .filter(
                    (p) =>
                      !projectFilter.trim() ||
                      p.project_name.toLowerCase().includes(projectFilter.toLowerCase()),
                  )
                  .map((project) => {
                    const link = liveLinks.get(project.id)
                    const isShareExpanded = expandedShareId === link?.share_id
                    return (
                      <div key={project.id} className="space-y-0">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => onProjectSelect(project.id)}
                            className={cn(
                              'flex-1 flex items-center gap-2 px-2 py-1.5 rounded-sm text-left transition-colors min-w-0',
                              'hover:bg-accent/50',
                              selectedProjectId === project.id && 'bg-accent',
                            )}
                            disabled={isLoadingProject}
                          >
                            <Folder
                              className={cn(
                                'h-3.5 w-3.5 shrink-0',
                                selectedProjectId === project.id
                                  ? 'text-primary'
                                  : 'text-muted-foreground',
                              )}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium truncate">{project.project_name}</p>
                              <p className="text-[10px] text-muted-foreground truncate">
                                {project.activity_count} activities
                              </p>
                            </div>
                            {selectedProjectId === project.id && (
                              <Check className="h-3.5 w-3.5 text-primary shrink-0" />
                            )}
                          </button>
                          {selectedProjectId === project.id && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <button
                                  className="p-1 rounded-sm shrink-0 hover:bg-destructive/10 hover:text-destructive transition-colors"
                                  title="Delete project"
                                  disabled={deletingProjectId === project.id}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {deletingProjectId === project.id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-3 w-3 text-muted-foreground" />
                                  )}
                                </button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="text-sm">
                                    Delete project
                                  </AlertDialogTitle>
                                  <AlertDialogDescription className="text-xs">
                                    Delete "{project.project_name}" and all its activities and
                                    exchanges? This cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel className="h-8 text-xs">
                                    Cancel
                                  </AlertDialogCancel>
                                  <AlertDialogAction
                                    className="h-8 text-xs bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    onClick={() =>
                                      handleDeleteProject(project.id, project.project_name)
                                    }
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                          {link ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                setExpandedShareId(isShareExpanded ? null : link.share_id)
                              }}
                              className={cn(
                                'p-1 rounded-sm shrink-0 transition-colors',
                                isShareExpanded ? 'bg-accent' : 'hover:bg-accent/50',
                              )}
                              title={link.is_public ? 'Public live link' : 'Private live link'}
                            >
                              {link.is_public ? (
                                <Globe className="h-3 w-3 text-green-500" />
                              ) : (
                                <LockKeyhole className="h-3 w-3 text-amber-500" />
                              )}
                            </button>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                createLiveLink(project.id)
                              }}
                              className="p-1 rounded-sm shrink-0 hover:bg-accent/50 transition-colors"
                              title="Create live link"
                              disabled={creatingLinkFor === project.id}
                            >
                              {creatingLinkFor === project.id ? (
                                <Loader2 className="h-3 w-3 text-muted-foreground animate-spin" />
                              ) : (
                                <LinkIcon className="h-3 w-3 text-muted-foreground" />
                              )}
                            </button>
                          )}
                        </div>
                        {/* Inline share controls */}
                        {link && isShareExpanded && (
                          <div className="ml-6 mr-1 mb-1 p-2 rounded-md bg-muted/50 border border-border/50 space-y-1.5">
                            <div className="flex items-center gap-1.5">
                              {link.is_public ? (
                                <Globe className="h-3 w-3 text-green-500 shrink-0" />
                              ) : (
                                <LockKeyhole className="h-3 w-3 text-amber-500 shrink-0" />
                              )}
                              <span className="text-[10px] text-muted-foreground flex-1">
                                {link.is_public ? 'Public' : 'Private'}
                              </span>
                              <button
                                onClick={() => toggleLinkVisibility(link.share_id)}
                                className={cn(
                                  'text-[10px] px-1.5 py-0.5 rounded transition-colors',
                                  link.is_public
                                    ? 'bg-amber-500/10 text-amber-500 hover:bg-amber-500/20'
                                    : 'bg-green-500/10 text-green-500 hover:bg-green-500/20',
                                )}
                              >
                                {link.is_public ? 'Make Private' : 'Make Public'}
                              </button>
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="flex-1 min-w-0 bg-background rounded px-1.5 py-0.5 overflow-hidden">
                                <span className="text-[10px] font-mono text-muted-foreground truncate block">
                                  {link.url}
                                </span>
                              </div>
                              <button
                                onClick={() => copyLinkUrl(link)}
                                className="p-0.5 rounded hover:bg-accent shrink-0 transition-colors"
                                title="Copy link"
                              >
                                {copiedShareId === link.share_id ? (
                                  <Check className="h-3 w-3 text-green-500" />
                                ) : (
                                  <Copy className="h-3 w-3 text-muted-foreground" />
                                )}
                              </button>
                              <a
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-0.5 rounded hover:bg-accent shrink-0 transition-colors"
                                title="Open live view"
                              >
                                <ExternalLink className="h-3 w-3 text-muted-foreground" />
                              </a>
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })
              )}
            </div>
            {/* Drag handle to resize the projects list vertically */}
            <div
              role="separator"
              aria-orientation="horizontal"
              onMouseDown={onResizeStart}
              title="Drag to resize"
              className="h-1 cursor-row-resize bg-border/40 hover:bg-primary/40 transition-colors"
            />
          </CollapsibleContent>
        </Collapsible>
      </div>

      {/* Current Project Activities */}
      {selectedProject && (
        <>
          {/* Search */}
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search activities..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8 pl-8 text-xs"
              />
            </div>
          </div>

          {/* Tree View */}
          <ScrollArea className="flex-1">
            <div className="p-2">
              {/* Project header */}
              <div className="flex items-center gap-1.5 px-1.5 py-1 mb-1">
                <FolderOpen className="h-3.5 w-3.5 text-primary" />
                <span className="text-xs font-medium truncate">{selectedProject.project_name}</span>
              </div>

              {filteredActivities.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground text-xs">
                  {activities.length === 0
                    ? 'No activities in this project'
                    : 'No matching activities'}
                </div>
              ) : (
                <div className="space-y-0.5 ml-2">
                  {filteredActivities.map((activity) => (
                    <ActivityTreeItem
                      key={activity.id}
                      activity={activity}
                      isExpanded={expandedActivities.has(activity.id)}
                      isSelected={selectedNodeId === activity.id}
                      onToggle={() => toggleActivity(activity.id)}
                      onSelect={() => selectNode(activity.id, 'activity')}
                      onSelectExchange={(exchangeId) => selectNode(exchangeId, 'exchange')}
                      selectedNodeId={selectedNodeId}
                    />
                  ))}
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Actions */}
          <div className="p-2 border-t border-border">
            <Button
              variant="outline"
              size="sm"
              className="w-full h-7 text-xs gap-1.5"
              onClick={() => setAddActivityOpen(true)}
            >
              <Plus className="h-3 w-3" />
              Add Activity
            </Button>
          </div>

          {/* Add Activity Dialog */}
          {selectedProjectId && (
            <AddActivityDialog
              open={addActivityOpen}
              onOpenChange={setAddActivityOpen}
              projectId={selectedProjectId}
              onCreated={() => onActivityCreated?.()}
            />
          )}
        </>
      )}

      {/* Empty state when no project selected */}
      {!selectedProject && (
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center text-muted-foreground">
            <Folder className="h-10 w-10 mx-auto mb-2 opacity-30" />
            <p className="text-xs">Select a project above</p>
          </div>
        </div>
      )}
    </div>
  )
}

interface ActivityTreeItemProps {
  activity: ActivityWithExchanges
  isExpanded: boolean
  isSelected: boolean
  onToggle: () => void
  onSelect: () => void
  onSelectExchange: (exchangeId: string) => void
  selectedNodeId: string | null
}

function ActivityTreeItem({
  activity,
  isExpanded,
  isSelected,
  onToggle,
  onSelect,
  onSelectExchange,
  selectedNodeId,
}: ActivityTreeItemProps) {
  const hasExchanges = activity.exchanges.length > 0

  return (
    <Collapsible open={isExpanded} onOpenChange={onToggle}>
      <div
        className={cn(
          'flex items-center gap-1 px-1.5 py-1 rounded-sm cursor-pointer hover:bg-accent/50 transition-colors',
          isSelected && 'bg-accent',
        )}
        onClick={onSelect}
      >
        {hasExchanges ? (
          <CollapsibleTrigger asChild onClick={(e) => e.stopPropagation()}>
            <button className="p-0.5 hover:bg-accent rounded-sm">
              {isExpanded ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
            </button>
          </CollapsibleTrigger>
        ) : (
          <span className="w-4" />
        )}

        {isExpanded ? (
          <FolderOpen className="h-3.5 w-3.5 text-primary shrink-0" />
        ) : (
          <Folder className="h-3.5 w-3.5 text-primary shrink-0" />
        )}

        <span className="text-xs truncate flex-1">{activity.name}</span>

        {activity.location && (
          <span className="text-[10px] text-muted-foreground">{activity.location}</span>
        )}
      </div>

      <CollapsibleContent>
        <div className="ml-6 border-l border-border pl-2 mt-0.5 space-y-0.5">
          {activity.exchanges.map((exchange) => (
            <div
              key={exchange.id}
              className={cn(
                'flex items-center gap-1.5 px-1.5 py-0.5 rounded-sm cursor-pointer hover:bg-accent/50 transition-colors text-xs',
                selectedNodeId === exchange.id && 'bg-accent',
              )}
              onClick={() => onSelectExchange(exchange.id)}
            >
              <ExchangeIcon type={exchange.exchange_type} />
              <span className="truncate flex-1">{exchange.name}</span>
              <span className="text-[10px] text-muted-foreground">
                {exchange.amount} {exchange.unit}
              </span>
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

function ExchangeIcon({ type }: { type: string }) {
  const iconClass = cn('h-3 w-3 shrink-0', {
    'text-blue-500': type === 'technosphere',
    'text-green-500': type === 'biosphere',
    'text-amber-500': type === 'production',
  })

  return type === 'production' ? (
    <Box className={iconClass} />
  ) : (
    <ArrowRightLeft className={iconClass} />
  )
}
